import { createAsyncThunk, createSlice, isAnyOf, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit'

import {
  createServiceActivity,
  deleteServiceActivity,
  getServiceActivities,
  updateServiceActivity,
  updateServiceActivityVisibility,
  type ServiceActivityDto,
  type ServiceActivityRequest,
} from '@/api/service-activity.api'
import { resetStore } from '@/store/actions/reset'
import { Status } from '@/types/general-types'
import { getErrorMessage } from '@/utils/utils'

interface ServiceActivitiesState {
  activities: ServiceActivityDto[]
  status: Status
  requestId: string | null
  saveStatus: Status
  /** Mutacja unieważniła trwające pobranie katalogu — jego odpowiedź sprzed zmiany zostanie pominięta. */
  listInvalidated: boolean
  /** Liczba trwających mutacji — ponowne pobranie dopiero po ostatniej. */
  pendingMutations: number
  /** Katalog do ponownego pobrania po zakończonej mutacji, która unieważniła poprzednie pobranie. */
  stale: boolean
}

const initialState: ServiceActivitiesState = {
  activities: [],
  status: 'idle',
  requestId: null,
  saveStatus: 'idle',
  listInvalidated: false,
  pendingMutations: 0,
  stale: false,
}

export const fetchServiceActivities = createAsyncThunk<ServiceActivityDto[]>(
  'serviceActivities/fetch',
  async (_, { rejectWithValue }) => {
    try {
      return await getServiceActivities()
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const createServiceActivityThunk = createAsyncThunk<ServiceActivityDto, ServiceActivityRequest>(
  'serviceActivities/create',
  async (payload, { rejectWithValue }) => {
    try {
      return await createServiceActivity(payload)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const updateServiceActivityThunk = createAsyncThunk<
  ServiceActivityDto,
  { id: string; payload: ServiceActivityRequest }
>('serviceActivities/update', async ({ id, payload }, { rejectWithValue }) => {
  try {
    return await updateServiceActivity(id, payload)
  } catch (error) {
    return rejectWithValue(getErrorMessage(error))
  }
})

export const setServiceActivityHidden = createAsyncThunk<ServiceActivityDto, { id: string; hidden: boolean }>(
  'serviceActivities/visibility',
  async ({ id, hidden }, { rejectWithValue }) => {
    try {
      return await updateServiceActivityVisibility(id, hidden)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const deleteServiceActivityThunk = createAsyncThunk<string, string>(
  'serviceActivities/delete',
  async (id, { rejectWithValue }) => {
    try {
      await deleteServiceActivity(id)
      return id
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

const replaceActivity = (state: ServiceActivitiesState, activity: ServiceActivityDto) => {
  const index = state.activities.findIndex((entry) => entry.id === activity.id)
  if (index === -1) state.activities.unshift(activity)
  else state.activities[index] = activity
}

const MUTATIONS = [
  createServiceActivityThunk,
  updateServiceActivityThunk,
  setServiceActivityHidden,
  deleteServiceActivityThunk,
] as const

const serviceActivitiesSlice = createSlice({
  name: 'serviceActivities',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchServiceActivities.pending, (state, action) => {
        state.status = 'loading'
        state.stale = false
        // Pobranie zaczęte w trakcie mutacji może odczytać bazę sprzed jej zapisu — odpowiedź jest pomijana,
        // a katalog wraca po ostatniej mutacji.
        if (state.pendingMutations > 0) {
          state.requestId = null
          state.listInvalidated = true
          return
        }
        state.requestId = action.meta.requestId
      })
      .addCase(fetchServiceActivities.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.status = 'succeeded'
        state.activities = action.payload
      })
      .addCase(fetchServiceActivities.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.status = 'failed'
      })
      .addCase(createServiceActivityThunk.pending, (state) => {
        state.saveStatus = 'loading'
      })
      .addCase(createServiceActivityThunk.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded'
        replaceActivity(state, action.payload)
      })
      .addCase(createServiceActivityThunk.rejected, (state) => {
        state.saveStatus = 'failed'
      })
      .addCase(updateServiceActivityThunk.pending, (state) => {
        state.saveStatus = 'loading'
      })
      .addCase(updateServiceActivityThunk.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded'
        replaceActivity(state, action.payload)
      })
      .addCase(updateServiceActivityThunk.rejected, (state) => {
        state.saveStatus = 'failed'
      })
      .addCase(setServiceActivityHidden.fulfilled, (state, action) => {
        replaceActivity(state, action.payload)
      })
      .addCase(deleteServiceActivityThunk.fulfilled, (state, action) => {
        state.activities = state.activities.filter((entry) => entry.id !== action.payload)
      })
      .addCase(resetStore, () => initialState)
      // Jak lista zleceń: katalog pobrany przed mutacją nadpisałby jej wynik, więc trwające pobranie jest pomijane,
      // a po zakończeniu mutacji ekran pobiera katalog ponownie.
      .addMatcher(isPending(...MUTATIONS), (state) => {
        state.pendingMutations += 1
        if (state.status !== 'loading' || state.requestId === null) return
        state.requestId = null
        state.listInvalidated = true
      })
      .addMatcher(isAnyOf(isFulfilled(...MUTATIONS), isRejected(...MUTATIONS)), (state) => {
        state.pendingMutations = Math.max(0, state.pendingMutations - 1)
        if (state.pendingMutations > 0 || !state.listInvalidated) return
        state.listInvalidated = false
        state.stale = true
      })
  },
})

export default serviceActivitiesSlice.reducer
